/**
 * ATTACHMENTS AS A READER SEES THEM AFTER THE TURN EXISTS.
 *
 * The composer's chips are draft controls. This component is the record's view:
 * it renders the metadata a message carries, fetches the text or the original
 * only when the Owner asks, and never states that a file was understood when
 * the extraction status says it was not.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { formatBytes, loadMaterialBlob, readMaterialText } from './chat';

const PREVIEW_CHARS = 100_000;

/** The subset of a material either the composer or the server projection can
 *  supply. Deliberately structural: a draft attachment and a recorded message
 *  attachment are the same thing to a reader. */
export interface DisplayMaterial {
	materialId: string;
	title: string;
	fileName?: string | null;
	mediaType?: string | null;
	byteSize: number;
	lineCount?: number | null;
	extractionStatus?: string | null;
	extractionNote?: string | null;
}

function isImage(material: DisplayMaterial): boolean {
	return (
		material.extractionStatus === 'IMAGE' ||
		(material.mediaType?.startsWith('image/') ?? false)
	);
}

function isProblematic(material: DisplayMaterial): boolean {
	return (
		material.extractionStatus === 'UNSUPPORTED' ||
		material.extractionStatus === 'FAILED' ||
		material.extractionStatus === 'IMAGE_ONLY'
	);
}

function MaterialItem({ material }: { material: DisplayMaterial }) {
	const [preview, setPreview] = useState<{ text: string; note: string | null } | null>(null);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const mounted = useRef(true);
	const imageUrlRef = useRef<string | null>(null);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			if (imageUrlRef.current !== null) URL.revokeObjectURL(imageUrlRef.current);
		};
	}, []);

	useEffect(() => {
		if (!isImage(material)) return;
		let cancelled = false;
		void loadMaterialBlob(material.materialId).then((result) => {
			if (cancelled || !mounted.current) return;
			if (!result.ok) return;
			const url = URL.createObjectURL(result.blob);
			imageUrlRef.current = url;
			setImageUrl(url);
		});
		return () => {
			cancelled = true;
		};
	}, [material.materialId, material.extractionStatus, material.mediaType]);

	async function show(): Promise<void> {
		if (preview !== null) {
			setPreview(null);
			return;
		}
		setBusy(true);
		setNotice(null);
		const result = await readMaterialText(material.materialId);
		if (!mounted.current) return;
		setBusy(false);
		if (!result.ok) {
			setNotice(`The material could not be read back: ${result.error.message}`);
			return;
		}
		setPreview({
			text: result.data.slice(0, PREVIEW_CHARS),
			note:
				result.data.length > PREVIEW_CHARS
					? `Showing the first ${PREVIEW_CHARS.toLocaleString()} of ${result.data.length.toLocaleString()} characters. The whole document is stored; Download returns the original.`
					: null,
		});
	}

	async function download(): Promise<void> {
		const result = await loadMaterialBlob(material.materialId);
		if (!result.ok) {
			setNotice(`The original could not be downloaded: ${result.error.message}`);
			return;
		}
		const url = URL.createObjectURL(result.blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = material.fileName ?? material.title;
		anchor.rel = 'noopener';
		document.body.appendChild(anchor);
		anchor.click();
		anchor.remove();
		setTimeout(() => URL.revokeObjectURL(url), 60_000);
	}

	return (
		<li
			class={`attachment-chip attachment-chip-record${isProblematic(material) ? ' is-warning' : ''}`}
			data-material-id={material.materialId}
		>
			<div class="attachment-chip-head">
				{imageUrl === null ? (
					<span class="attachment-kind" aria-hidden="true">
						{material.extractionStatus ?? 'FILE'}
					</span>
				) : (
					<img class="attachment-thumb" src={imageUrl} alt={`Attached image ${material.fileName ?? material.title}`} />
				)}
				<span class="attachment-title">{material.fileName ?? material.title}</span>
				<span class="muted small">
					{[
						material.mediaType?.split(';')[0],
						formatBytes(material.byteSize),
						typeof material.lineCount === 'number' && material.lineCount > 0
							? `${material.lineCount.toLocaleString()} lines`
							: null,
					]
						.filter((part): part is string => part !== null && part !== undefined && part.length > 0)
						.join(' · ')}
				</span>
			</div>
			{isProblematic(material) && material.extractionNote !== null && material.extractionNote !== undefined && (
				<p class="attachment-warning small">{material.extractionNote}</p>
			)}
			<div class="attachment-chip-actions">
				{!isImage(material) && (
					<button type="button" class="button ghost small" onClick={() => void show()} disabled={busy}>
						{busy ? 'Reading…' : preview === null ? 'Show' : 'Hide'}
					</button>
				)}
				{(material.fileName !== null && material.fileName !== undefined) && (
					<button type="button" class="button ghost small" onClick={() => void download()}>
						Download
					</button>
				)}
			</div>
			{notice !== null && <p class="composer-notice small">{notice}</p>}
			{preview !== null && (
				<div class="attachment-preview-wrap">
					{preview.note !== null && <p class="muted small">{preview.note}</p>}
					<pre class="attachment-preview">{preview.text}</pre>
				</div>
			)}
		</li>
	);
}

export function MaterialList({
	materials,
	label = 'Attachments on this turn',
}: {
	materials: readonly DisplayMaterial[];
	label?: string;
}) {
	if (materials.length === 0) return null;
	return (
		<ul class="attachments attachments-record" aria-label={label}>
			{materials.map((material) => (
				<MaterialItem key={material.materialId} material={material} />
			))}
		</ul>
	);
}
